import React, { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { addDoc, updateDoc, doc, collection, getDocs } from "firebase/firestore";
import { BsPersonFill } from "react-icons/bs";

const indicatifs = [
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+32", label: "🇧🇪 +32" },
  { code: "+41", label: "🇨🇭 +41" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
];

// Couleurs par défaut si Firebase n'est pas disponible
const DEFAULT_TAG_COLORS = [
  { id: 1, nom: 'Bleu', codeHex: '#2563eb', ordre: 1 },
  { id: 2, nom: 'Rouge', codeHex: '#ef4444', ordre: 2 },
  { id: 3, nom: 'Vert', codeHex: '#22c55e', ordre: 3 },
  { id: 4, nom: 'Jaune', codeHex: '#eab308', ordre: 4 },
  { id: 5, nom: 'Orange', codeHex: '#f97316', ordre: 5 },
  { id: 6, nom: 'Violet', codeHex: '#8b5cf6', ordre: 6 },
  { id: 7, nom: 'Rose', codeHex: '#ec4899', ordre: 7 },
  { id: 8, nom: 'Turquoise', codeHex: '#06b6d4', ordre: 8 },
  { id: 9, nom: 'Gris', codeHex: '#6b7280', ordre: 9 },
  { id: 10, nom: 'Noir', codeHex: '#111827', ordre: 10 },
  { id: 11, nom: 'Marron', codeHex: '#92400e', ordre: 11 },
  { id: 12, nom: 'Blanc', codeHex: '#f3f4f6', ordre: 12 }
];

export default function ProprietaireFormModal({ proprietaire, onClose }) {
  const [form, setForm] = useState({
    civilite: "",
    nom: "",
    prenom: "",
    adresse: "",
    complementAdresse: "",
    codePostal: "",
    ville: "",
    pays: "",
    email: "",
    dateNaissance: "",
    commentaires: "",
    indicatifTel1: "+33",
    telephone1: "",
    indicatifTel2: "",
    telephone2: "",
    entreprise: "",
    siret: "",
    tva: "",
    tagCouleur: "", // ID numérique en string (nouveau système)
    ...proprietaire,
  });

  const [tagColors, setTagColors] = useState([]);

  // Charger les couleurs de tags depuis Firebase
  useEffect(() => {
    async function fetchTagColors() {
      try {
        const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
        let colorsData = [];
        
        tagColorsSnap.forEach(doc => {
          const data = doc.data();
          if (data.id && data.codeHex) {
            colorsData.push({ 
              id: data.id,
              nom: data.nom || '',
              codeHex: data.codeHex,
              ordre: data.ordre || data.id
            });
          }
        });

        // Trier par ordre
        colorsData.sort((a, b) => a.ordre - b.ordre);

        // Si pas de données Firebase, utiliser les couleurs par défaut
        if (colorsData.length === 0) {
          console.log('Aucune couleur trouvée dans tagCouleursAnimaux, utilisation des couleurs par défaut');
          colorsData = DEFAULT_TAG_COLORS;
        }

        setTagColors(colorsData);
      } catch (error) {
        console.error('Erreur lors du chargement des couleurs depuis tagCouleursAnimaux:', error);
        setTagColors(DEFAULT_TAG_COLORS);
      }
    }
    fetchTagColors();
  }, []);

  // Migration automatique : si on a tagCouleurNom mais pas tagCouleur
  useEffect(() => {
    if (proprietaire?.tagCouleurNom && !proprietaire?.tagCouleur) {
      // Essayer de trouver l'ID correspondant au nom
      const foundColor = tagColors.find(tc => tc.nom === proprietaire.tagCouleurNom);
      if (foundColor) {
        setForm(prev => ({ ...prev, tagCouleur: foundColor.id.toString() }));
      }
    }
  }, [proprietaire, tagColors]);

  // Obtenir la couleur du tag sélectionné
  const getSelectedTagColor = () => {
    if (!form.tagCouleur) return null;
    const numericId = parseInt(form.tagCouleur);
    return tagColors.find(tc => tc.id === numericId);
  };

  const selectedTagColor = getSelectedTagColor();

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function handleTagSelect(tagId) {
    setForm(prev => ({ ...prev, tagCouleur: tagId.toString() }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Nettoyer les anciennes données (supprimer tagCouleurNom si présent)
    const cleanForm = { ...form };
    delete cleanForm.tagCouleurNom;
    
    if (form.id) {
      await updateDoc(doc(db, "proprietaires", form.id), cleanForm);
    } else {
      await addDoc(collection(db, "proprietaires"), cleanForm);
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-30 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-0 w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh]">
        <div className="sticky top-0 bg-white p-6 pb-2 rounded-t-2xl border-b"></div>
        <form onSubmit={handleSubmit} className="p-6 pt-2 space-y-4">
          {/* Civilité + Nom + Prénom */}
          <div className="flex flex-col md:flex-row gap-3">
            <select name="civilite" className="border px-2 py-1 rounded flex-1" value={form.civilite} onChange={handleChange}>
              <option value="">Civilité</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
              <option value="Mx">Mx</option>
            </select>
            <input className="border px-2 py-1 rounded flex-1" name="nom" value={form.nom} onChange={handleChange} placeholder="Nom" required />
            <input className="border px-2 py-1 rounded flex-1" name="prenom" value={form.prenom} onChange={handleChange} placeholder="Prénom" required />
          </div>

          <hr />

          {/* Adresse */}
          <div className="space-y-2">
            <input className="border w-full px-2 py-1 rounded" name="adresse" value={form.adresse} onChange={handleChange} placeholder="Adresse" />
            <input className="border w-full px-2 py-1 rounded" name="complementAdresse" value={form.complementAdresse} onChange={handleChange} placeholder="Complément adresse" />
            <div className="flex flex-col md:flex-row gap-3">
              <input className="border px-2 py-1 rounded flex-1" name="codePostal" value={form.codePostal} onChange={handleChange} placeholder="Code Postal" />
              <input className="border px-2 py-1 rounded flex-1" name="ville" value={form.ville} onChange={handleChange} placeholder="Ville" />
              <input className="border px-2 py-1 rounded flex-1" name="pays" value={form.pays} onChange={handleChange} placeholder="Pays" />
            </div>
          </div>

          <hr />

          {/* Email, Date naissance */}
          <div className="flex flex-col md:flex-row gap-3">
            <input className="border px-2 py-1 rounded flex-1" name="email" value={form.email} onChange={handleChange} placeholder="E-mail" type="email" />
            <input className="border px-2 py-1 rounded flex-1" name="dateNaissance" value={form.dateNaissance} onChange={handleChange} type="date" placeholder="Date de naissance" />
          </div>

          {/* Commentaires */}
          <textarea className="border w-full px-2 py-1 rounded" name="commentaires" value={form.commentaires} onChange={handleChange} placeholder="Commentaires" rows={2} />

          <hr />

          {/* Téléphones */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex flex-row gap-2 flex-1">
              <select name="indicatifTel1" className="border px-2 py-1 rounded" value={form.indicatifTel1} onChange={handleChange}>
                {indicatifs.map(i => <option key={i.code} value={i.code}>{i.label}</option>)}
              </select>
              <input className="border px-2 py-1 rounded flex-1" name="telephone1" value={form.telephone1} onChange={handleChange} placeholder="Téléphone 1" />
            </div>
            <div className="flex flex-row gap-2 flex-1">
              <select name="indicatifTel2" className="border px-2 py-1 rounded" value={form.indicatifTel2} onChange={handleChange}>
                <option value="">---</option>
                {indicatifs.map(i => <option key={i.code} value={i.code}>{i.label}</option>)}
              </select>
              <input className="border px-2 py-1 rounded flex-1" name="telephone2" value={form.telephone2} onChange={handleChange} placeholder="Téléphone 2" />
            </div>
          </div>

          <hr />

          {/* Entreprise / SIRET / TVA */}
          <div className="flex flex-col md:flex-row gap-3">
            <input className="border px-2 py-1 rounded flex-1" name="entreprise" value={form.entreprise} onChange={handleChange} placeholder="Entreprise" />
            <input className="border px-2 py-1 rounded flex-1" name="siret" value={form.siret} onChange={handleChange} placeholder="N° SIRET" />
            <input className="border px-2 py-1 rounded flex-1" name="tva" value={form.tva} onChange={handleChange} placeholder="N° TVA" />
          </div>

          <hr />

          {/* TAG COULEUR AVEC ICONES DE PERSONNES */}
          <div className="mb-4">
            <label className="block mb-2 font-medium">Tag couleur propriétaire</label>
            <div className="border rounded-lg p-3 bg-gray-50/50">
              {/* Sélection actuelle */}
              {selectedTagColor ? (
                <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <BsPersonFill 
                      className="w-6 h-6" 
                      style={{ color: selectedTagColor.codeHex }}
                    />
                    <span className="font-medium text-gray-900">{selectedTagColor.nom}</span>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">ID: {selectedTagColor.id}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTagSelect('')}
                    className="text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200 text-gray-500 text-center">
                  Aucun tag sélectionné
                </div>
              )}

              {/* Grille de sélection des couleurs avec icônes de personnes */}
              <div className="grid grid-cols-6 gap-2">
                {tagColors.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagSelect(tag.id)}
                    className={`relative w-10 h-10 rounded-lg border-2 hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center ${
                      selectedTagColor?.id === tag.id 
                        ? 'border-gray-700 ring-2 ring-gray-400 ring-offset-1 bg-gray-100' 
                        : 'border-gray-300 hover:border-gray-500 bg-white'
                    }`}
                    title={`${tag.nom} (ID: ${tag.id})`}
                  >
                    <BsPersonFill 
                      className="w-6 h-6" 
                      style={{ color: tag.codeHex }}
                    />
                    {selectedTagColor?.id === tag.id && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-800 rounded-full border border-white" />
                    )}
                  </button>
                ))}
              </div>
              
              {tagColors.length === 0 && (
                <div className="text-center text-gray-500 py-6 bg-white rounded-lg border border-gray-200">
                  <div className="text-gray-400 text-lg mb-2">⚙️</div>
                  <p className="font-medium">Aucune couleur de tag configurée</p>
                  <p className="text-sm mt-1">Configurez les couleurs dans les Paramètres</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-6">
            <button
              type="button"
              className="px-6 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium shadow-sm"
            >
              {form.id ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}